import propTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import styles from './styles';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import Divider from '@material-ui/core/Divider';
import GreyUserI from 'assets/images/user-photo-placeholder.svg';
import LocationPin from 'components/LocationPin/LocationPin';
import { compareWorkersCrewLeaderThenAlpha } from 'helpers/_helpers';
import { agoDate, shortDate } from 'helpers/_date-helpers';

import { useGetEquipmentInfo } from 'pages/ScheduleV6/components/hooks/useGetEquipmentInfo';
import { useDateSettingsFormat } from 'common/useDateSettingsFormat';
import { useTimeSettingsFormat } from 'common/useTimeSettingsFormat';
import { useCanEditWorklog, useCanEditWorklogCrewLeader } from 'common/hooks/use-can-edit-worklog';
import { removeResources } from 'store/schedule/schedulePageOperation';
import { postWorklogCL } from 'store/currentWorklog/operations';
import { generateHoursObject, getEffectiveStartTime } from 'helpers/helpers';
import { getSheetEventTitle, getStatusColor, getStatusText } from 'pages/ScheduleV6/utils';
import { SCHEDULE_VIEW_TYPES } from 'pages/ScheduleV6/constants/constants';


const WorklogDetailsCard = ({
  classes,
  sheet,
  userId = '',
  close,
  view,
}) => {
  const dispatch = useDispatch();
  const { getEquipmentInfo } = useGetEquipmentInfo();
  const { dateFormatWithTime } = useDateSettingsFormat();
  const getTimeFormatHook = useTimeSettingsFormat();


  const hasAccess = useCanEditWorklog(sheet);
  const canEditCrewLeader = useCanEditWorklogCrewLeader(sheet);

  const handleRemoveResource = (type, id) => () => {
    if (hasAccess) dispatch(removeResources({ sheetId: sheet._id, [type]: [id] }));
  };

  const setForeman = (worker) => async () => {
    if (!canEditCrewLeader(worker)) return;
    const sheetId = sheet._id;
    const userId = worker._id;
    const value = !worker.foreman;

    await dispatch(postWorklogCL({ foreman: value }, sheetId, userId));
  };

  const getImageSrc = () => {
    return GreyUserI;
  };

  const getSheetStatus = () => {
    const { published, submittedAt, canceledAt } = sheet;
    if (!published) return 'Draft';
    if (submittedAt) {
      return `Submitted ${agoDate(submittedAt, null, dateFormatWithTime)}`;
    } else if (canceledAt) {
      return 'Canceled';
    } else return 'Published';
  };

  const getJobNumber = () => sheet.project.jobNumber;

  const getFormattedHoursRange = () => {
    const { createdAt, hours } = sheet;
    if (!hours || hours?.start === undefined || hours?.end === undefined) return;
    const { hourStart, minuteStart, amPmStart, dayStart, hourEnd, minuteEnd, amPmEnd, dayEnd } =
      generateHoursObject(hours.start, hours.end, createdAt, getTimeFormatHook);

    const startObj = [null, hourStart, minuteStart, amPmStart];
    const endObj = [null, hourEnd, minuteEnd, amPmEnd];

    const start = `${getTimeFormatHook(startObj).formattedTime} ${shortDate(
      createdAt,
      dayStart,
      getTimeFormatHook().localDayMonth
    )}`;
    const end = `${getTimeFormatHook(endObj).formattedTime} ${shortDate(
      createdAt,
      dayEnd,
      getTimeFormatHook().localDayMonth
    )}`;

    return `${start} \u2013 ${end}`;
  };

  const findLocation = (sheet, locationId, fieldName) => {
    const allPlaces =
      fieldName === 'sheetRallyPoints'
        ? [...(sheet?.sheetRallyPoints || []), ...(sheet.project?.rallyPoints || [])]
        : [...(sheet?.sheetLocations || []), ...(sheet.project?.locations || [])];
    return allPlaces.find(({ _id } = {}) => (_id === locationId));
  };

  const sheetLocation = (sheet, isStandalone, locationId, fieldName) => {
    if (!sheet.project) return '';
    const location = findLocation(sheet, locationId, fieldName);
    if (isStandalone) {
      if (!location) return '';
      const locationZip = location.zipcode ? `, ${location.zipcode}` : '';
      return location.name || `${location.address}${locationZip}`;
    }
    const nameOrAddress = location.name || location.address;
    return location && nameOrAddress ? ', ' + nameOrAddress : '';
  };

  const getIndividualStartTime = () => {
    const workerId = userId;
    const worker = sheet.workers.find(({ _id }) => _id === workerId);

    const startTime = (worker && worker.startTime)
      ? getEffectiveStartTime(worker.startTime, worker.startTimeDate)
      : getEffectiveStartTime(sheet.startTime, sheet.startTimeDate);

    const startHoursObj = [null, startTime.hour, startTime.minute, null];

    return getTimeFormatHook(startHoursObj).formattedTime;
  };


  const getStatusComponent = (sheet) => {
    const color = getStatusColor(sheet);
    const text = getStatusText(sheet);
    return (
      <div className={classnames(classes.sheetStatusWrapper)}>
        <Typography className={classnames(classes.sheetStatusText)} style={{ color: color, borderColor: color }}>
          {text}
        </Typography>
      </div>
    );
  };

  return (
    <Card className={classes.card}>
      <Grid container className={classes.paddingBox}>
        <Grid
          item
          xs={12}
          className={classnames(classes.paddingBottom, classes.inlineFlex, classes.alignStart)}
        >
          <div className={classes.photoWrapper}>
            <img
              src={getImageSrc(sheet.project.contractor && sheet.project.contractor.logo)}
              className={classes.photo}
            />
          </div>
          <div className={classnames(classes.flexColumn, classes.selfCenter)}>
            <Typography variant="h6" color="textPrimary" className={classes.name}>
              {sheet.project.contractor && sheet.project.contractor.name}
            </Typography>
            {sheet.forceAccount && sheet.forceAccount !== 'No' && (
              <div className={classes.flexWrapper}>
                <Typography
                  variant="body2"
                  className={classnames(classes.forceAccount)}
                >
                  T&M: {sheet.forceAccount}
                </Typography>
              </div>
            )}
          </div>
          {view !== SCHEDULE_VIEW_TYPES.monthly && (
            <IconButton
              className={classes.closeButton}
              style={{ height: 34, width: 34, padding: 5 }}
              onClick={close}
              aria-label="Close"
              disableRipple
            >
              <CloseIcon />
            </IconButton>
          )}
        </Grid>
        <Grid item xs={12} className={classnames(classes.paddingBottom, classes.projectNameBlock)}>
          <Typography variant="h6" color="textPrimary" style={{ display: 'flex' }}>
            {getSheetEventTitle(sheet)}
          </Typography>
            {getStatusComponent(sheet)}
        </Grid>
        <Grid item xs={12} className={classes.paddingBottom}>
          <Divider />
        </Grid>
        <Grid item xs={12} className={classes.paddingBottom}>
          <div
            className={classnames(
              classes.inlineFlex,
              classes.paddingBottom,
              classes.alignStart
            )}
          >
            <Typography
              variant="body1"
              color="textSecondary"
              className={classnames(
                classes.mediumFontWeight,
                sheet.locations && sheet.locations.length ? classes.paddingTopSmall : null
              )}
            >
              #{getJobNumber()}
            </Typography>
            <div
              className={classnames(
                classes.flexColumn,
                classes.widthAuto,
                classes.overflowHidden
              )}
            >
              {sheet.rallyPoints &&
                !!sheet.rallyPoints.length &&
                !!sheet.rallyPoints[0] &&
                sheet.rallyPoints.map((locationId) => (
                  <div
                    className={classnames(
                      classes.locationFlex,
                      classes.paddingBottomSmall,
                      classes.fullWidth
                    )}
                    key={`${locationId}`}
                  >
                    <LocationPin location={findLocation(sheet, locationId, 'sheetRallyPoints')} />
                    <Typography
                      variant="body1"
                      color="textPrimary"
                      className={classes.location}
                    >
                      {sheetLocation(sheet, true, locationId, 'sheetRallyPoints')}
                    </Typography>
                  </div>
                ))}
              {sheet.locations &&
                !!sheet.locations.length &&
                !!sheet.locations[0] &&
                sheet.locations.map((locationId) => (
                  <div
                    className={classnames(
                      classes.locationFlex,
                      classes.paddingBottomSmall,
                      classes.fullWidth
                    )}
                    key={`${locationId}`}
                  >
                    <LocationPin location={findLocation(sheet, locationId, 'sheetLocations')} />
                    <Typography
                      variant="body1"
                      color="textPrimary"
                      className={classes.location}
                    >
                      {sheetLocation(sheet, true, locationId, 'sheetLocations')}
                    </Typography>
                  </div>
                ))}
              {!Boolean(sheet.locations.length) && !Boolean(sheet.rallyPoints.length) && (
                <div className={classes.locationFlex}>
                  <Typography variant="body1" color="textPrimary" className={classes.location}>
                    N/A
                  </Typography>
                </div>
              )}
            </div>
          </div>
          <Typography
            variant="body1"
            color="textSecondary"
            className={classnames(
              classes.mediumFontWeight,
              getSheetStatus() === 'Canceled' ? classes.redText : classes.yellowText
            )}
          >
            {getSheetStatus()}
          </Typography>
        </Grid>
        <Grid item xs={12} className={classes.paddingBottom}>
          <Divider />
        </Grid>
        {!!sheet.startTime && (
          <Grid item xs={12} md={6} className={classes.paddingBottom}>
            <Typography
              variant="body1"
              color="textSecondary"
              className={classnames(classes.regularFontWeight, classes.paddingBottomSmall)}
            >
              Start Time:
            </Typography>
            <Typography variant="body1" color="textPrimary">
              {getIndividualStartTime()}
            </Typography>
          </Grid>
        )}
        {!!sheet.travelTime && (
          <Grid item xs={12} md={6} className={classes.paddingBottom}>
            <Typography
              variant="body1"
              color="textSecondary"
              className={classnames(classes.regularFontWeight, classes.paddingBottomSmall)}
            >
              Travel Time:
            </Typography>
            <Typography variant="body1" color="textPrimary">
              {sheet.travelTime.hours}:{sheet.travelTime.minutes}
            </Typography>
          </Grid>
        )}
        {sheet?.hours?.start && sheet?.hours?.start && (
          <Grid item xs={12} className={classes.paddingBottom}>
            <Typography
              variant="body1"
              color="textSecondary"
              className={classnames(classes.regularFontWeight, classes.paddingBottomSmall)}
            >
              On Job Time:
            </Typography>
            <Typography variant="body1" color="textPrimary">
              {getFormattedHoursRange()}
            </Typography>
          </Grid>
        )}

        <Grid item xs={12} className={classes.paddingBottom}>
          <Divider />
        </Grid>

        <Grid xs={12}>
          {Boolean(sheet.notes) && (
            <Grid item className={classes.paddingBottom}>
              <Typography
                variant="body1"
                color="textSecondary"
                className={classnames(classes.marginBottomSmall, classes.paddingBottomSmall)}
              >
                Note:
              </Typography>
              <Typography
                variant="body1"
                color="textPrimary"
                className={classnames(classes.notesText, classes.marginBottom)}
              >
                {sheet.notes}
              </Typography>
            </Grid>
          )}
          {Boolean(sheet.workers.length) && (
            <Grid item>
              <Typography
                variant="body1"
                color="textSecondary"
                className={classes.marginBottomSmall}
              >
                People:
              </Typography>
              <div className={classnames(classes.marginBottomSmall)}>
                {sheet.workers.slice().sort(compareWorkersCrewLeaderThenAlpha).map((worker) => (
                  <div
                    key={worker._id}
                    className={classnames(classes.marginBottom, classes.itemWrapperContent)}
                  >
                    <div>
                      <div
                        className={classnames(
                          classes.flexBox,
                          classes.justifyContentStart,
                          classes.marginBottomSmall
                        )}
                      >
                        <div
                          className={classnames(
                            classes.photoWrapperContent,
                            classes[`${worker?.shifts?.timeOfDay?.toLowerCase()}Border`]
                          )}
                        >
                          <img src={GreyUserI} className={classes.photoWorker} />
                        </div>
                        <Typography
                          variant="body1"
                          color="textPrimary"
                          className={classnames(
                            classes.username,
                            classes[worker?.shifts?.timeOfDay?.toLowerCase()]
                          )}
                        >
                          {worker.username}
                        </Typography>
                      </div>
                    </div>
                    <div>
                      <Button
                        variant="text"
                        className={
                          worker.foreman ? classes.activeButton : classes.inactiveButton
                        }
                        onClick={setForeman(worker)}
                        disableRipple
                        disabled={!canEditCrewLeader(worker)}
                      >
                        (CL)
                      </Button>
                      {hasAccess && (
                        <IconButton
                          className={classes.closeButton}
                          onClick={handleRemoveResource('workers', worker._id)}
                          aria-label="Close"
                          disableRipple
                          disableTouchRipple
                        >
                          <CloseIcon />
                        </IconButton>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <Grid item xs={12} className={classes.paddingBottom}>
                <Divider />
              </Grid>
            </Grid>
          )}
          {Boolean(sheet.equipment.length) && (
            <Grid item>
              <Typography
                variant="body1"
                color="textSecondary"
                className={classnames(classes.marginBottomSmall, classes.paddingBottomSmall)}
              >
                Equipment:
              </Typography>
              <div className={classnames(classes.marginBottomSmall)}>
                {sheet.equipment.map((item) => (
                  <div
                    key={item._id}
                    className={classnames(
                      classes.marginBottom,
                      classes.itemWrapperContent,
                      classes.marginBottomSmall
                    )}
                  >
                    <div>
                      <div
                        className={classnames(
                          classes.equipmentBlock,
                          getEquipmentInfo(item._id, 'className')
                        )}
                      >
                        <div
                          className={classes.equipmentNumber}
                          style={{ backgroundColor: getEquipmentInfo(item._id, 'color') }}
                        >
                          {getEquipmentInfo(item._id, 'number') || ''}
                        </div>
                        <div
                          className={classes.equipmentName}
                          style={{ color: getEquipmentInfo(item._id, 'color') }}
                        >
                          {getEquipmentInfo(item._id, 'name')}
                        </div>
                      </div>
                    </div>
                    <div>
                      {hasAccess && (
                        <IconButton
                          className={classes.closeButton}
                          onClick={handleRemoveResource('equipment', item._id)}
                          aria-label="Close"
                          disableRipple
                          disableTouchRipple
                        >
                          <CloseIcon />
                        </IconButton>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <Grid item xs={12} className={classes.paddingBottom}>
                <Divider />
              </Grid>
            </Grid>
          )}
        </Grid>

        <Grid container item xs={12}>
          <Typography
            variant="body1"
            color="textSecondary"
            className={classnames(
              classes.regularFontWeight,
              !sheet.ownerUsername && classes.paddingBottom
            )}
          >
            Created on {agoDate(sheet.createdAtDate, null, dateFormatWithTime)}
          </Typography>
          &nbsp;<span className={classnames(classes.secondaryText)}>&#8226;</span>&nbsp;
          {sheet.ownerUsername && (
            <Typography
              variant="body1"
              color="textSecondary"
              className={classnames(classes.regularFontWeight, classes.paddingBottom)}
            >
              {sheet.ownerUsername}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Card>
  )
}

WorklogDetailsCard.propTypes = {
  sheet: propTypes.object.isRequired,
  close: propTypes.func.isRequired,
  userId: propTypes.string,
  hasEditAccess: propTypes.bool,
  view: propTypes.string,
}

export default withStyles(styles)(WorklogDetailsCard)